import { SetStateAction } from "jotai";
import { Dispatch, useEffect, useRef } from "react";

import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { usePartWithChildren } from "#modules/parts/view/usePartWithChildren";
import { findChild } from "#modules/similarPart/domain/findChild";
import { JobStatusSchema, PartTypeSchema } from "shared";

import { computeAssemblyComplexity } from "../domain/computeAssemblyComplexity";
import { isAllAssemblyAnalyzed } from "./isAllAssemblyAnalyzed";

const MAX_COMPLEXITY = 50;

export const useAssembly = ({
  part,
  setPart,
  defaultPartId,
}: {
  part: PartDto | null;
  setPart: Dispatch<SetStateAction<PartDto | null>>;
  defaultPartId: string | undefined;
}) => {
  const partId = part?.id || defaultPartId;

  const isEmbeddingLoading =
    !part?.hasTopologicalEmbedding ||
    part.topologicalEmbeddingStatus === JobStatusSchema.enum.STANDBY ||
    part.topologicalEmbeddingStatus === JobStatusSchema.enum.PROCESSING;

  const isFeatureAnalyzeLoading =
    part?.featuresStatus === JobStatusSchema.enum.STANDBY ||
    part?.featuresStatus === JobStatusSchema.enum.PROCESSING;

  const isPreviewError = part?.previewStatus === JobStatusSchema.enum.ERROR;

  const { part: assembly, isLoading } = usePartWithChildren(defaultPartId, {
    refetchInterval: (query) => {
      const data = query.state.data;

      const isAssemblyAnalyzing = !data || !isAllAssemblyAnalyzed(data);

      const isNotAnAssembly =
        part?.type === PartTypeSchema.enum.ASSEMBLY && data?.children.length === 0;

      const shouldRefetch =
        isAssemblyAnalyzing || ((isNotAnAssembly || isFeatureAnalyzeLoading) && !isPreviewError);

      const assemblyComplexity = computeAssemblyComplexity(data);

      return shouldRefetch ? (assemblyComplexity > MAX_COMPLEXITY ? 10000 : 2000) : false;
    },
    enabled: !!partId,
  });

  const onAssemblyUpdateRef = useRef<(p: typeof assembly) => void>(undefined);
  onAssemblyUpdateRef.current = (p) => {
    if (!p) {
      return;
    }
    if (part === null || !part.urlPng3D) {
      setPart(p);
    } else if (part.rootParentId !== p.id) {
      setPart(p);
    }
    if (isEmbeddingLoading) {
      const updatedPart = findChild(part?.id, p);
      if (updatedPart?.hasTopologicalEmbedding) {
        setPart(updatedPart);
      }
    }
  };

  useEffect(() => {
    onAssemblyUpdateRef.current?.(assembly);
  }, [assembly]);

  const isImageLoading = !!assembly && !assembly.urlPng3D;

  return {
    assembly,
    isAssemblyLoading: isLoading || isImageLoading,
    isAssemblyAnalyzing: !isAllAssemblyAnalyzed(assembly),
  };
};
